import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { LeftMenu } from '~/editor/leftMenu/LeftMenu';
import { MainContent } from '~/editor/mainContent/MainContent';
import {
  useEditorStore,
  EditorSequence,
  EditorChapter,
  SequenceItem
} from '~/shell/Store';
import { CloseButton } from '~/editor/leftMenu/CloseButton';
import { Spinner } from '~/ui/spinner/Spinner';
import { useToastStore } from '~/ui/toast/toastStore';
import { ToastSeverity } from '~/ui/toast/types';
import { useLocation } from 'react-router-dom';
import { CourseCreated } from '~/editor/ui/CourseCreated/CourseCreated';
import Tooltips from '~/editor/tooltips/Tooltips';
import posthog from 'posthog-js';
import { GlobalRewritePortal } from '~/training/sequence/templates/items/text/GlobalRewritePortal';
import { setupBeforeUnloadHandler } from '~/editor/lib/beforeUnloadHandler';

const Wrapper = styled.div`
  color: #444746;
  height: 100vh;
  display: flex;
  flex-direction: column;
`;

const Content = styled.div`
  display: flex;
  flex: 1;
`;

interface SequenceData {
  sequence: EditorSequence;
  sequenceMetadata?: {
    canUndo: boolean;
    canRedo: boolean;
  };
}

const generateExitFallbackPath = (
  companyId: string | null,
  trainingId: string | null
) => {
  const resultPath = [
    companyId ? `companies/${companyId}` : null,
    companyId && !trainingId ? 'course_authoring/trainings' : null,
    trainingId ? `trainings/${trainingId}` : null
  ]
    .filter(Boolean)
    .join('/');

  return `/${resultPath}`;
};

export const Editor = () => {
  const {
    sequence,
    setTraining,
    setSequence,
    apiService,
    collectIds,
    setApiService,
    setShowTooltips,
    setFeatureFlags,
    hasFeatureFlag,
    setUndoRedoState,
    fetchGlobalPlaceholders,
    fetchTemplates
  } = useEditorStore();
  const [showMenu, setShowMenu] = useState(true);
  const [showLoader, setShowLoader] = useState(true);
  const [mode, setMode] = useState<'editor' | 'preview' | 'translation'>(
    'editor'
  );
  const [showTooltipSection, setShowTooltipSection] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const token = queryParams.get('token');
  const company_id = queryParams.get('company_id');
  const training_id = queryParams.get('training_id');
  const exitRedirectUrl =
    queryParams.get('exitRedirectUrl') ||
    queryParams.get('trainingCatalog') ||
    generateExitFallbackPath(company_id, training_id);
  const newTraining = queryParams.get('new_training');
  const product_tour = queryParams.get('product_tour');
  const publishButtonTitle = queryParams.get('publishButtonTitle');
  const publishRedirectUrl = queryParams.get('publishRedirectUrl');

  useEffect(() => {
    if (product_tour == 'true' && sequence && !showTooltipSection) {
      setShowTooltips(true);
      setShowTooltipSection(true);
    }
  }, [product_tour, sequence, showTooltipSection]);

  const [showCourseCreated, setShowCourseCreated] = useState(
    newTraining == 'true'
  );

  //i know we need to do this in the backend, but for now we are doing it here
  if (sequence?.templateName === 'Images7') {
    sequence?.items?.push({
      type: 'BACKGROUND',
      value: 'var(--color-pink-lt)'
    } as SequenceItem);
  }

  const getTrainingAndSequences = () => {
    if (!apiService) {
      console.error('ApiService is not initialized.');
      return;
    }

    if (!training_id) {
      return;
    }

    apiService
      .getCourseInfo(training_id)
      .then(data => {
        const firstSequence = data.training.chapters.reduce(
          (
            found: EditorChapter['chapterSequences'][number] | null,
            chapter: EditorChapter
          ) => {
            if (found) return found;
            return chapter.chapterSequences?.[0] || null;
          },
          null
        );

        const sequencePromises = firstSequence
          ? [apiService.getSequenceInfo(firstSequence.sequence.id)]
          : [];

        Promise.all<SequenceData>(sequencePromises).then(sequenceDataArray => {
          setShowLoader(false);
          setSequence(sequenceDataArray[0]?.sequence);

          if (
            sequenceDataArray[0]?.sequenceMetadata &&
            hasFeatureFlag('course_editor_backend_undo_redo')
          ) {
            setUndoRedoState(
              sequenceDataArray[0].sequenceMetadata.canUndo,
              sequenceDataArray[0].sequenceMetadata.canRedo
            );
          }

          const updatedTraining = {
            ...data.training,
            chapters: data.training.chapters.map((chapter: EditorChapter) => ({
              ...chapter,
              chapterSequences: chapter?.chapterSequences?.map(
                (
                  chapterSequence: EditorChapter['chapterSequences'][number]
                ) => {
                  const sequenceData = sequenceDataArray.find(
                    (seqData: SequenceData) =>
                      seqData.sequence.id === chapterSequence.sequence.id
                  );

                  if (sequenceData) {
                    return {
                      ...chapterSequence,
                      sequence: {
                        ...chapterSequence.sequence,
                        ...sequenceData.sequence
                      }
                    };
                  }
                  return chapterSequence;
                }
              )
            }))
          };

          setTraining(updatedTraining);
        });
      })
      .catch(error => {
        useToastStore.getState().add({
          severity: ToastSeverity.Error,
          message: error
        });
      });
  };

  useEffect(() => {
    if (token && company_id) {
      setApiService(token, company_id);
    }
  }, [token, company_id]);

  useEffect(() => {
    if (apiService) {
      apiService
        .getCompanyWithFeatureFlags()
        .then(data => {
          if (data && data.company && data.company.featureFlags) {
            setFeatureFlags(data.company.featureFlags);
          } else {
            setFeatureFlags([]);
          }
          getTrainingAndSequences();
          fetchGlobalPlaceholders();
          fetchTemplates();
        })
        .catch(error => {
          console.error('Failed to fetch feature flags:', error);
          setFeatureFlags([]);
          getTrainingAndSequences();
          fetchGlobalPlaceholders();
          fetchTemplates();
        });
    }
  }, [apiService]);

  useEffect(() => {
    setShowMenu(mode === 'editor' || mode === 'translation');

    if (mode === 'preview' && apiService) {
      getTrainingAndSequences();
    }
  }, [mode, apiService]);

  useEffect(() => {
    if (
      window &&
      window.location &&
      (window?.location?.hostname === 'localhost' ||
        window?.location?.hostname === 'app.easyllama.local')
    ) {
      return;
    }

    if (
      typeof window !== 'undefined' &&
      !(posthog as { __loaded: boolean }).__loaded
    ) {
      posthog.init('phc_qvfwkRLvz4JIch2ShspuB7F7bmDZjO9t9tE08yNc0dq', {
        api_host: 'https://us.posthog.com',
        session_recording: {}
      });
    }
  }, []);

  useEffect(() => {
    const cleanup = setupBeforeUnloadHandler();
    return cleanup;
  }, []);

  return (
    <Wrapper>
      {!showCourseCreated && <Tooltips />}
      {showCourseCreated && (
        <CourseCreated onClose={() => setShowCourseCreated(false)} />
      )}
      <GlobalRewritePortal />
      {showLoader ? (
        <Spinner />
      ) : (
        <Content>
          {showMenu && (
            <LeftMenu
              mode={mode === 'translation' ? 'translation' : 'editor'}
            />
          )}
          {(mode === 'editor' || mode === 'translation') &&
            !showCourseCreated && (
              <CloseButton showMenu={showMenu} setShowMenu={setShowMenu} />
            )}
          {sequence && sequence.id && (
            <MainContent
              key={collectIds(sequence) + '-' + sequence?.items?.length}
              {...sequence}
              mode={mode}
              setMode={setMode}
              exitRedirectUrl={exitRedirectUrl}
              showButtons={!showCourseCreated}
              publishButtonTitle={publishButtonTitle}
              publishRedirectUrl={publishRedirectUrl}
              showMenu={showMenu}
            />
          )}
        </Content>
      )}
    </Wrapper>
  );
};
